import {
  AuthFill,
  Button,
  DropDown,
  Email,
  FirstName,
  LastName,
  MarketingConsent,
  Tel,
  Title,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign } from '@contexts/useCampaign'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import { closeOverlay, getModels, getPageData } from '@utils/helpers'
import { createYupSchema } from '@utils/helpers/createYupSchema'
import { formEvent } from '@utils/helpers/tracking'
import { PrizeDrawCmsSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import type { ParsedUrlQuery } from 'querystring'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'

const PrizeDraw = ({ content, tracking, brand, query }) => {
  const [submitResponse, setSubmitResponse] = useState('')
  const { hidden_fields: hiddenFields, fields } = content
  const campaign = useCampaign()

  let initialValues = {}
  const usedFields = []

  // build initialValues from cms fields
  PrizeDrawCmsSchema.forEach((item) => {
    const found = fields.some((el) => el.input_type === item.field)
    if (found) {
      if (item.field?.includes('_optional')) {
        // eslint-disable-next-line no-param-reassign
        item.id = item.id.replace('_optional', '')
      }
      initialValues[item.id] = item.value || ''
    }
  })
  // remove empty response codes fields
  Object.keys(hiddenFields).forEach((key) => {
    if (hiddenFields[key] === '' || hiddenFields[key] === null) {
      delete hiddenFields[key]
    }
  })

  // combine all fields
  initialValues = {
    ...initialValues,
    ...hiddenFields,
    is_lexus: brand === 'lexus',
    is_dealer: false,
    dealer_name: '',
  }

  // map fields with schemas
  PrizeDrawCmsSchema.forEach((item) => {
    const inputField = { ...item }
    if (initialValues[inputField.id] !== undefined) {
      // map required fields
      const found = fields.some(
        (el) => el.optional && el.input_type === inputField.field,
      )
      if (found) {
        inputField.validations = inputField.validations.filter(
          (el) => el.input_type !== 'required',
        )
      }
      usedFields.push(inputField)
    }
  })

  // create global schema
  const yepSchema = usedFields.reduce(createYupSchema, {})
  const validateSchema = yup.object().shape(yepSchema)

  const methods = useForm({
    resolver: yupResolver(validateSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.brochure) {
      methods.setValue('campaign_response_code', campaign.brochure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (query?.email) {
      methods.setValue('email', query.email)
    }

    if (query?.title) {
      methods.setValue('title', query.title)
    }

    if (query?.firstname || query?.first_name) {
      methods.setValue('first_name', query.firstname || query.first_name)
    }

    if (query?.lastname || query?.last_name) {
      methods.setValue('last_name', query.lastname || query.last_name)
    }
  }, [query])

  const DropDownOnChange = (e) => {
    methods.setValue('preference', e.target.value)
    methods.trigger('preference')
  }

  // Fields builder
  const InputRenderer = ({ fieldComponents }: any) => {
    const Components = {
      DropDown,
      Email,
      FirstName,
      LastName,
      Tel,
      Tel_optional: Tel,
      Title,
    }
    return fieldComponents.map((field, key) => {
      const FieldComp = Components[field.input_type] || null
      const props = {}
      if (FieldComp) {
        if (field.input_type.includes('_optional')) {
          props.optional = true
        }
        if (field.helper) props.helper = field.helper
        if (field.input_type === 'DropDown') {
          props.options = content.options
          props.name = 'preference'
          props.label = 'Event Selection'
          props.onChange = DropDownOnChange
        }
      }

      return <FieldComp key={key} {...props} />
    })
  }

  const PrizeDrawFields = useMemo(
    () => <InputRenderer fieldComponents={content.fields} />,
    content.fields,
  )

  const onSubmit = async (data: any) => {
    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/prize-draw`,
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
      .then(() => {
        formEvent({
          conversion: 1,
          name: tracking.form,
          step: 'form-submit',
          variant: tracking.variant,
          page: tracking.page,
        })
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent({
          conversion: 0,
          name: tracking.form,
          step: 'form-error',
          variant: tracking.variant,
          page: tracking.page,
        })
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        background={content.background}
        color={content?.font_colour}
      />
      <FormWrapper>
        <FormStep
          title={content.details.title}
          subtitle={content.details.subtitle}
          step={1}
          active={true}
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              {PrizeDrawFields}
              <MarketingConsent />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

interface Params extends ParsedUrlQuery {
  id: string
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'prize-draw'
  const { id } = context.params as Params
  const content = await import(
    `../../content/${brand}/forms/prize-draw/cms-forms/${id}.md`
  ).catch(() => null)
  const settings = await import(
    `../../content/${brand}/settings/global.md`
  ).catch(() => null)

  // redirect if not active and redirect url exist
  if (!content.attributes?.active && content.attributes?.redirect) {
    const destination = new URL(content.attributes?.redirect)

    Object.entries(context.query).forEach((element) => {
      if (element.length > 1 && element[0] !== 'id') {
        const key = element[0]
        const value = element[1] as string
        destination.searchParams.append(key, value)
      }
    })

    return {
      redirect: {
        destination: destination.href,
        permanent: true,
      },
    }
  }

  // if no content/disabled - show 404
  if (
    !content ||
    content.attributes.fields.length === 0 ||
    !content.attributes?.active
  ) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      models: getModels(brand, form),
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: id,
        form: `lead-${id}`,
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context, form)),
    },
  }
}

PrizeDraw.whyDidYouRender = true

export default PrizeDraw
